import { useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Separator } from '../../components/ui/separator';
import {
  Plus, Send, Inbox, Mail, Search
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export const Emails = ({ emails, contacts, onRefresh }) => {
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [composeOpen, setComposeOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("sent");
  const [formData, setFormData] = useState({
    to_email: "", subject: "", body: "", contact_id: "none"
  });

  const filteredEmails = emails.filter(e => e.folder === activeTab);

  const handleSend = async () => {
    try {
      const payload = { ...formData };
      if (!payload.contact_id || payload.contact_id === "none") delete payload.contact_id;

      await axios.post(`${API}/emails`, payload);
      setComposeOpen(false);
      setFormData({ to_email: "", subject: "", body: "", contact_id: "none" });
      onRefresh();
    } catch (error) {
      console.error("Error sending email:", error);
    }
  };

  const getContactByEmail = (email) => {
    return contacts.find(c => c.email === email);
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="emails-view">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-cabinet text-3xl font-bold text-slate-900">Emails</h2>
          <p className="text-slate-500 font-manrope">Manage your email communications</p>
        </div>
        <Button 
          data-testid="compose-email-btn"
          onClick={() => setComposeOpen(true)} 
          className="bg-royal-blue-700 hover:bg-royal-blue-800"
        >
          <Plus size={18} className="mr-2" /> Compose
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Email List */}
        <Card className="lg:col-span-1 border-slate-200">
          <CardHeader className="pb-2">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="sent" data-testid="sent-tab">
                  <Send size={14} className="mr-2" /> Sent
                </TabsTrigger>
                <TabsTrigger value="inbox" data-testid="inbox-tab">
                  <Inbox size={14} className="mr-2" /> Inbox
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </CardHeader>
          <CardContent className="p-0">
            <ScrollArea className="h-[500px]">
              <div className="divide-y divide-slate-100">
                {filteredEmails.map((email) => (
                  <div 
                    key={email.id}
                    onClick={() => setSelectedEmail(email)}
                    data-testid={`email-item-${email.id}`}
                    className={`p-4 cursor-pointer hover:bg-slate-50 transition-colors ${
                      selectedEmail?.id === email.id ? 'bg-royal-blue-50' : ''
                    }`}
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-royal-blue-500 to-purple-500 flex items-center justify-center text-white font-bold text-xs">
                        {email.to_email.charAt(0).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-slate-900 truncate">{email.to_email}</p>
                        <p className="text-xs text-slate-500">
                          {new Date(email.sent_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <p className="font-medium text-sm text-slate-800 truncate">{email.subject}</p>
                    <p className="text-sm text-slate-500 truncate">{email.body}</p>
                  </div>
                ))}
                {filteredEmails.length === 0 && (
                  <div className="p-8 text-center text-slate-500">
                    No emails in this folder
                  </div>
                )}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Email Reader */}
        <Card className="lg:col-span-2 border-slate-200">
          <CardContent className="p-6">
            {selectedEmail ? (
              <div className="space-y-4">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-cabinet text-xl font-bold text-slate-900">{selectedEmail.subject}</h3>
                    <div className="flex items-center gap-2 mt-2">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-royal-blue-500 to-purple-500 flex items-center justify-center text-white font-bold">
                        {selectedEmail.to_email.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">{selectedEmail.to_email}</p>
                        <p className="text-sm text-slate-500">
                          {new Date(selectedEmail.sent_at).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => setSelectedEmail(null)}>
                    <X size={18} />
                  </Button>
                </div>
                <Separator />
                <div className="prose prose-slate max-w-none">
                  <p className="whitespace-pre-wrap text-slate-700">{selectedEmail.body}</p>
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-[400px] text-slate-400">
                <Mail size={48} className="mb-4" />
                <p>Select an email to read</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Compose Dialog */}
      <Dialog open={composeOpen} onOpenChange={setComposeOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-cabinet">Compose Email</DialogTitle>
            <DialogDescription>Create and send a new email</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="to_email">To *</Label>
                <Input 
                  id="to_email"
                  type="email"
                  placeholder="recipient@example.com"
                  data-testid="email-to-input"
                  value={formData.to_email} 
                  onChange={(e) => setFormData({...formData, to_email: e.target.value})} 
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="contact">Link to Contact</Label>
                <Select value={formData.contact_id} onValueChange={(v) => {
                  const contact = contacts.find(c => c.id === v);
                  setFormData({
                    ...formData, 
                    contact_id: v,
                    to_email: (contact && v !== "none") ? contact.email : formData.to_email
                  });
                }}>
                  <SelectTrigger data-testid="email-contact-select">
                    <SelectValue placeholder="Select contact" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {contacts.map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.name} ({c.email})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="subject">Subject *</Label>
              <Input 
                id="subject"
                placeholder="Email subject"
                data-testid="email-subject-input"
                value={formData.subject} 
                onChange={(e) => setFormData({...formData, subject: e.target.value})} 
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="body">Message *</Label>
              <Textarea 
                id="body"
                placeholder="Write your message..."
                rows={10}
                data-testid="email-body-input"
                value={formData.body} 
                onChange={(e) => setFormData({...formData, body: e.target.value})} 
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setComposeOpen(false)}>Cancel</Button>
            <Button 
              data-testid="send-email-btn"
              onClick={handleSend} 
              className="bg-royal-blue-700 hover:bg-royal-blue-800"
              disabled={!formData.to_email || !formData.subject || !formData.body}
            >
              <Send size={16} className="mr-2" /> Send Email
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

